// Phase 2 — frontend error reporter.
//
// Posts to /api/feedback/auto-error. Loop-protected (we never report the
// reporter's own failures), client-side rate-throttled (50/min cap so a
// runaway error loop can't DoS the API), and best-effort: any failure here
// is swallowed silently — user-facing UI never sees this code crash.

export const AUTO_ERROR_PATH = '/api/feedback/auto-error';

// Client-side throttle. Server also enforces 50/IP/hr.
const MAX_PER_WINDOW = 50;
const WINDOW_MS = 60 * 1000;
const sentTimestamps = [];

let sessionId = null;
function getSessionId() {
  if (sessionId) return sessionId;
  try {
    sessionId = window.sessionStorage.getItem('civix_session_id');
    if (!sessionId) {
      sessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      window.sessionStorage.setItem('civix_session_id', sessionId);
    }
  } catch (_) {
    sessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
  return sessionId;
}

function apiBase() {
  const fromEnv = import.meta.env?.VITE_API_URL;
  return (fromEnv || '/api').replace(/\/$/, '');
}

function withinThrottle() {
  const now = Date.now();
  while (sentTimestamps.length && sentTimestamps[0] < now - WINDOW_MS) {
    sentTimestamps.shift();
  }
  if (sentTimestamps.length >= MAX_PER_WINDOW) return false;
  sentTimestamps.push(now);
  return true;
}

// Skips 401/402 because 401 = session expiry (already handled by redirect to
// /login) and 402 = billing-flow business response (toasted in api.js, not a
// bug). 5xx and other 4xx still report. Detected via the decorated axios
// message signature ("401: ..." / "API 402: ..." / "402: ...") since the
// reporter only receives the stringified message, not the original error.
function isExpectedBusinessStatus(errorMessage) {
  if (typeof errorMessage !== 'string') return false;
  return /(^|[\s:])(?:API )?4(?:01|02)(?::|\s|$)/.test(errorMessage);
}

export function reportFrontendError({ errorMessage, stack, source }) {
  try {
    if (isExpectedBusinessStatus(errorMessage)) return;
    if (!withinThrottle()) return;
    const url = `${apiBase()}${AUTO_ERROR_PATH.replace(/^\/api/, '')}`;
    const token = (() => {
      try { return localStorage.getItem('civix_token'); } catch (_) { return null; }
    })();

    const body = JSON.stringify({
      errorMessage: typeof errorMessage === 'string' ? errorMessage.slice(0, 500) : 'Unknown',
      stack: typeof stack === 'string' ? stack.slice(0, 8000) : '',
      route: typeof window !== 'undefined' ? window.location.pathname : '',
      sessionId: getSessionId(),
      browserUA: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      ts: new Date().toISOString(),
      source: source || 'window_error',
    });

    // Prefer fetch (we can attach Authorization). Fallback to sendBeacon for
    // pagehide. Beacon doesn't support Authorization headers — we accept the
    // anonymous report on close.
    // Mark the request so the axios interceptor's loop guard recognizes it
    // even if the URL is proxied / rewritten (e.g. /proxy/feedback/auto-error).
    const headers = {
      'Content-Type': 'application/json',
      'X-Civix-Auto-Error': '1',
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    if (typeof fetch === 'function') {
      // Use keepalive so the request survives a tab close mid-flight.
      fetch(url, { method: 'POST', headers, body, keepalive: true }).catch(() => {});
    } else if (navigator?.sendBeacon) {
      try { navigator.sendBeacon(url, new Blob([body], { type: 'application/json' })); } catch (_) {}
    }
  } catch (_) { /* swallowed */ }
}

// Use a window-level guard rather than a module-scoped flag — under Vite HMR
// the module is re-evaluated on every save, which would otherwise stack a
// fresh pair of error/unhandledrejection listeners on every reload (causing
// duplicate Feedback records in dev).
export function installGlobalErrorListeners() {
  if (typeof window === 'undefined') return;
  if (window.__civixErrorListenersInstalled) return;
  window.__civixErrorListenersInstalled = true;

  const onError = (ev) => {
    try {
      const msg = ev?.message || ev?.error?.message || 'Window error';
      const stack = ev?.error?.stack || '';
      reportFrontendError({ errorMessage: msg, stack, source: 'window_error' });
    } catch (_) {}
  };
  const onRejection = (ev) => {
    try {
      const reason = ev?.reason;
      const msg = reason?.message || (typeof reason === 'string' ? reason : 'Unhandled rejection');
      const stack = reason?.stack || '';
      reportFrontendError({ errorMessage: msg, stack, source: 'unhandled_rejection' });
    } catch (_) {}
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  // HMR cleanup: drop the listeners + the install flag when this module is
  // hot-replaced so the next eval can re-attach a fresh set without stacking.
  if (typeof import.meta !== 'undefined' && import.meta.hot) {
    import.meta.hot.dispose(() => {
      try {
        window.removeEventListener('error', onError);
        window.removeEventListener('unhandledrejection', onRejection);
      } catch (_) {}
      window.__civixErrorListenersInstalled = false;
    });
  }
}
